import { useEffect, useRef, useState, type ReactNode } from "react";
import { Star } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

import fpautomation from "@/assets/testimonials/fpautomation.jpeg";
import hireadev from "@/assets/testimonials/hireadev.png";
import satta from "@/assets/testimonials/satta.png";
import secondself from "@/assets/testimonials/secondself.jpeg";
import trane from "@/assets/testimonials/trane.jpeg";
import virtue from "@/assets/testimonials/virtue.jpeg";

type Testimonial = {
  headline: string;
  quote: string; // separate paragraphs with \n\n
  author: string;
  role: string;
  company: string;
  column: "left" | "right";
  image?: string;
  // Substrings of `quote` to render bold. Must match exactly (case-sensitive).
  highlights?: string[];
};

// Real Trustpilot reviews — placeholders use [Client Name] markers.
// `column` controls which side the card appears on; long reviews on one side,
// short stack on the other, alternating down the section.
const TESTIMONIALS: Testimonial[] = [
  {
    headline: "Thorough Audit",
    quote:
      "Before I started working with Paul, I thought I was using AI pretty well. Then I went through his audit, which was more thorough than I expected. He asked about every part of my business. We identified that I could save the most time on content creation and answering customers across my store platforms. He set up AI agents for both and explained how they worked. Highly recommend!",
    author: "Claire T.",
    role: "Owner",
    company: "Satta",
    column: "left",
    image: satta,
    highlights: [
      "save the most time on content creation and answering customers across my store platforms",
    ],
  },
  {
    headline: "A must try for those looking to dip their toes into AI",
    quote:
      "As an executive in a commercial HVAC business, finding a partner that truly understands AI, construction, and HVAC is rare. Most alternatives are either nonexistent or priced for enterprise-level firms that overlook companies our size.\n\nWhat initially stood out was their industry report on AI in HVAC and commercial real estate. It reflected a deep understanding of where AI can create real value in our space. That carried through in our engagement. Our discussions were rooted in a strong understanding of the industry, making their recommendations practical and directly applicable to our business.\n\nWith AI rapidly being introduced across the HVAC and construction industries, it’s critical to keep pace and not fall behind. Their team helped position us to do exactly that.\n\nTheir team met us where we were, recognizing that becoming AI-ready is a step-by-step process. The roadmap they delivered was clear, tailored, and realistic, balancing near-term wins with long-term strategy.\n\nOverall, they offer a level of industry-specific insight and practicality that is difficult to find, especially for companies operating between small-scale providers and large enterprise consulting firms.",
    author: "Travis T.",
    role: "Executive",
    company: "HVAC",
    column: "left",
    image: trane,
    highlights: [
      "Most alternatives are either nonexistent or priced for enterprise-level firms that overlook companies our size.",
      "recognizing that becoming AI-ready is a step-by-step process. The roadmap they delivered was clear, tailored, and realistic, balancing near-term wins with long-term strategy.",
    ],
  },
  {
    headline:
      "Paul helped us cut through the noise and unlock real AI-driven efficiency",
    quote:
      "What sets Paul apart is his ability to cut through the noise and ask the right questions, specifically, why we were doing things the way we were. Rather than just presenting solutions, he worked alongside our team to educate them on how AI could genuinely enhance their productivity, freeing them up to focus on higher-value areas of our consulting operations. The results spoke for themselves: with Paul's guidance, we were able to reduce our development times our consulting team spends on coding by upwards of 30% while improving the quality leading to less time spent on peer reviews enabling our team to take on more projects. If you're a business looking to integrate AI in a meaningful, practical way not just for the sake of it, Paul at Wolf CTO is exactly who you want in your corner.",
    author: "Alp A.",
    role: "Managing Director",
    company: "Kinetiq",
    column: "left",
    image: fpautomation,
    highlights: [
      "reduce our development times our consulting team spends on coding by upwards of 30% while improving the quality leading to less time spent on peer reviews enabling our team to take on more projects.",
    ],
  },
  {
    headline:
      "I can't recommend Paul enough - His AI audit saved me HOURS in dev time and is helping generate significantly more revenue because of a simplified system!!",
    quote:
      "I hired Paul for an AI audit of the platform I built using Lovable and n8n.\n\nI kept trying to figure out how to make my platform easier to scale, simpler to onboard clients into, and less development-heavy long term, but I kept overcomplicating the solution because I was so ingrained in the build.\n\nWhat I appreciated most about Paul was that he approached everything from both a business and technology perspective. He didn't come in trying to sell me some massive rebuild or convince me I needed to scrap everything. Instead, he asked strategic questions that uncovered the actual thing holding the system back.\n\nAnd ironically? The solution ended up being a relatively minor tweak!\n\nBut that one tweak is saved me HOURS of development time, HOURS onboarding new clients, and adds a significant amount of money because it allows me to scale faster.\n\nHonestly, it was one of those “how did I not see this?” moments… which is exactly why having someone outside your ecosystem matters.\n\nIf you're building with AI, automation, workflows, or custom systems and feel stuck trying to scale what you've already built, I highly recommend working with Paul. His ability to quickly identify operational friction and simplify systems without over engineering them is incredibly valuable.\n\nVery thankful for his guidance and I'll absolutely be working with him again in the future!",
    author: "Ruth S.",
    role: "Founder",
    company: "SecondSelf",
    column: "right",
    image: secondself,
    highlights: [
      "he approached everything from both a business and technology perspective.",
      "saved me HOURS of development time, HOURS onboarding new clients, and adds a significant amount of money",
    ],
  },
  {
    headline: "AI Agents and Revenue Operations",
    quote:
      "Paul quickly understood how our Finance and Revenue Operations team supported a rapidly scaling healthcare business. He identified opportunities where AI could reduce manual work, improve efficiency, and free the team to focus on higher value activities across forecasting, reporting, pipeline management, and execution. What stood out was his ability to prioritize the highest ROI workflows first and turn those ideas into systems our team could use. This was especially valuable because the business was scaling quickly but our team capacity was constrained. His work helped us operate more efficiently, reduce repetitive manual effort, and build a foundation we can grow on.",
    author: "Robert S.",
    role: "Finance",
    company: "Virtue Health",
    column: "right",
    image: virtue,
    highlights: [
      "prioritize the highest ROI workflows first and turn those ideas into systems our team could use",
    ],
  },
  {
    headline: "Tech + business",
    quote:
      "What surprised me most wasn't the AI expertise (which is obviously deep), it was how well he understands business. Most technical people can build the thing. Paul actually gets why it matters, who it's for, and how it makes money. That is exactly why his clients see the kind of results they do. Highly recommend.",
    author: "Gabriel C.",
    role: "Founder",
    company: "Development Agency",
    column: "right",
    image: hireadev,
    highlights: [
      "gets why it matters, who it's for, and how it makes money. That is exactly why his clients see the kind of results they do.",
    ],
  },
];

// One shared observer for every FadeIn on the page — cheaper than N observers
// and avoids stacked layout reads when many cards mount at once.
let sharedObserver: IntersectionObserver | null = null;
const callbacks = new WeakMap<Element, () => void>();

const getObserver = () => {
  if (sharedObserver) return sharedObserver;
  sharedObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          callbacks.get(entry.target)?.();
          sharedObserver!.unobserve(entry.target);
          callbacks.delete(entry.target);
        }
      }
    },
    { threshold: 0.15, rootMargin: "0px 0px -10% 0px" },
  );
  return sharedObserver;
};

const FadeIn = ({
  children,
  delay = 0,
}: {
  children: ReactNode;
  delay?: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = getObserver();
    callbacks.set(el, () => setVisible(true));
    observer.observe(el);
    return () => {
      observer.unobserve(el);
      callbacks.delete(el);
    };
  }, []);

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 700ms ease-out ${delay}ms, transform 700ms ease-out ${delay}ms`,
      }}>
      {children}
    </div>
  );
};

const Stars = () => (
  <div className="flex gap-0.5 mb-3" role="img" aria-label="5 out of 5 stars">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className="w-4 h-4 fill-gold text-gold"
        aria-hidden="true"
      />
    ))}
  </div>
);

const initialsOf = (name: string) =>
  name
    .replace(/[^A-Za-z\s]/g, "")
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const renderWithHighlights = (
  text: string,
  highlights?: string[],
): ReactNode => {
  if (!highlights || highlights.length === 0) return text;
  const re = new RegExp(`(${highlights.map(escapeRegex).join("|")})`, "g");
  const parts = text.split(re);
  return parts.map((part, i) =>
    highlights.includes(part) ? (
      <strong key={i} className="font-semibold text-navy">
        {part}
      </strong>
    ) : (
      part
    ),
  );
};

const TestimonialCard = ({
  headline,
  quote,
  author,
  role,
  company,
  image,
  highlights,
}: Testimonial) => (
  <div className="bg-background border border-border rounded-xl p-6 shadow-sm h-full flex flex-col">
    <Stars />
    <h3 className="font-semibold text-navy mb-2">{headline}</h3>
    <div className="text-foreground leading-relaxed mb-5 flex-1 space-y-3">
      {quote.split("\n\n").map((para, i) => (
        <p key={i}>{renderWithHighlights(para, highlights)}</p>
      ))}
    </div>
    <div className="flex items-center gap-3 pt-4 border-t border-border">
      {image ? (
        <img
          src={image}
          alt=""
          className="w-10 h-10 rounded-full object-cover"
        />
      ) : (
        <div className="w-10 h-10 rounded-full bg-gold/20 text-gold flex items-center justify-center font-semibold text-sm">
          {initialsOf(author)}
        </div>
      )}
      <div className="leading-tight">
        <p className="font-semibold text-navy text-sm">{author}</p>
        <p className="text-xs text-body-gray">
          {role}, {company}
        </p>
      </div>
    </div>
  </div>
);

type TestimonialsProps = {
  // When true, hides the left sticky heading and the closing pull-quote/CTA.
  // Used on /get-started where only the cards are wanted.
  compact?: boolean;
};

const Testimonials = ({ compact = false }: TestimonialsProps) => {
  const cards = (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 items-start">
      <div className="flex flex-col gap-5">
        {TESTIMONIALS.filter((t) => t.column === "left").map((t, i) =>
          compact ? (
            <TestimonialCard key={`l-${i}`} {...t} />
          ) : (
            <FadeIn key={`l-${i}`} delay={i * 80}>
              <TestimonialCard {...t} />
            </FadeIn>
          ),
        )}
      </div>
      <div className="flex flex-col gap-5">
        {TESTIMONIALS.filter((t) => t.column === "right").map((t, i) =>
          compact ? (
            <TestimonialCard key={`r-${i}`} {...t} />
          ) : (
            <FadeIn key={`r-${i}`} delay={i * 80 + 40}>
              <TestimonialCard {...t} />
            </FadeIn>
          ),
        )}
      </div>
    </div>
  );

  return (
    <section className="py-20 lg:py-28 bg-secondary">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {compact ? (
          <div className="max-w-5xl mx-auto">{cards}</div>
        ) : (
          <div className="grid lg:grid-cols-[4fr_8fr] gap-12 lg:gap-20">
            {/* Left — sticky message */}
            <div className="lg:sticky lg:top-24 lg:self-start">
              <span className="text-sm font-semibold uppercase tracking-wider text-gold mb-4 block">
                Testimonials
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif leading-tight mb-5">
                What owners and operators say
              </h2>
              <p className="text-lg text-body-gray leading-relaxed mb-6">
                The problems they walked in with, and the fixes that followed
              </p>
              <Button
                variant="gold"
                size="lg"
                className="text-white font-bold bg-gradient-to-br from-gold to-orange-400 hover:from-gold-dark hover:to-orange-500"
                asChild>
                <Link to="/case-study">Read the Case Studies</Link>
              </Button>
            </div>

            {/* Right — two columns of testimonials with explicit column placement.
                Each column flows top-to-bottom; mix short and long cards across
                columns to create the alternating asymmetric pattern. */}
            {cards}
          </div>
        )}

        {!compact && (
          <>
            {/* Closing pull quote + CTA to Trustpilot */}
            <FadeIn>
              <div className="mt-20 lg:mt-32 max-w-3xl mx-auto text-center">
                <div className="flex justify-center mb-5">
                  <Stars />
                </div>
                <blockquote className="font-serif text-2xl sm:text-3xl lg:text-4xl leading-snug text-foreground mb-6">
                  I highly recommend working with Paul. His ability to quickly
                  identify operational friction and simplify systems without
                  over engineering them is incredibly valuable.
                </blockquote>
                <p className="text-body-gray mb-8">— Ruth S, via Trustpilot</p>
                <Button
                  variant="gold"
                  size="xl"
                  className="text-white font-bold bg-gradient-to-br from-gold to-orange-400 hover:from-gold-dark hover:to-orange-500"
                  asChild>
                  <a
                    href="https://www.trustpilot.com/review/wolf-cto.com"
                    target="_blank"
                    rel="noopener noreferrer">
                    More Reviews
                  </a>
                </Button>
              </div>
            </FadeIn>

            {/* Section divider — gold ornament between testimonials and industries */}
            <div
              className="mt-20 lg:mt-28 flex items-center justify-center gap-4"
              aria-hidden="true">
              <span className="h-px w-16 sm:w-24 bg-gradient-to-r from-transparent to-gold/40" />
              <div className="flex gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-gold/70" />
                <span className="w-2 h-2 rounded-full bg-gold" />
                <span className="w-1.5 h-1.5 rounded-full bg-gold/70" />
              </div>
              <span className="h-px w-16 sm:w-24 bg-gradient-to-l from-transparent to-gold/40" />
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default Testimonials;
